import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { 
  Calendar, BarChart2, TrendingUp, FileText, CheckCircle, Lock, Users, CreditCard, Plus
} from 'lucide-react';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';

const ProjectReports = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState({
    type_periode: 'mensuel',
    date_debut: '',
    date_fin: '',
    notes: ''
  });

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/project-finances/${projectId}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReports(res.data.reports || []);
      setCanManage(res.data.can_manage || false);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const fetchReportDetail = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/project-finances/${projectId}/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedReport(res.data.report);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/project-finances/${projectId}/reports`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowModal(false);
      resetForm();
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const handleValidate = async (reportId) => {
    if (!(await confirm({ title: 'Confirmation', message: 'Valider ce bilan et calculer les distributions de bénéfices ?', type: 'warning' }))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/project-finances/${projectId}/reports/${reportId}/validate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchReports();
      if (selectedReport && selectedReport.id === reportId) {
        fetchReportDetail(reportId);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      type_periode: 'mensuel',
      date_debut: '',
      date_fin: '',
      notes: ''
    });
  };

  const handlePeriodTypeChange = (type) => {
    const now = new Date();
    let debut, fin;
    
    if (type === 'mensuel') {
      debut = new Date(now.getFullYear(), now.getMonth(), 1);
      fin = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (type === 'trimestriel') {
      const quarter = Math.floor(now.getMonth() / 3);
      debut = new Date(now.getFullYear(), quarter * 3, 1);
      fin = new Date(now.getFullYear(), quarter * 3 + 3, 0);
    } else {
      debut = new Date(now.getFullYear(), 0, 1);
      fin = new Date(now.getFullYear(), 11, 31);
    }
    
    setFormData(prev => ({
      ...prev,
      type_periode: type,
      date_debut: debut.toISOString().split('T')[0],
      date_fin: fin.toISOString().split('T')[0]
    }));
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const periodLabels = {
    mensuel: { label: 'Mensuel', icon: <Calendar className="w-4 h-4" />, color: 'bg-blue-100 text-blue-700' },
    trimestriel: { label: 'Trimestriel', icon: <BarChart2 className="w-4 h-4" />, color: 'bg-purple-100 text-purple-700' },
    annuel: { label: 'Annuel', icon: <TrendingUp className="w-4 h-4" />, color: 'bg-green-100 text-green-700' }
  };

  const statusLabels = {
    brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: <FileText className="w-4 h-4" /> },
    valide: { label: 'Validé', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-4 h-4" /> },
    cloture: { label: 'Clôturé', color: 'bg-indigo-100 text-indigo-700', icon: <Lock className="w-4 h-4" /> }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Link to={`/projects/${projectId}/finances`} className="text-indigo-200 hover:text-white text-sm mb-2 inline-flex items-center gap-1">
            ← Retour au dashboard
          </Link>
          <h1 className="text-3xl font-bold flex items-center gap-2"><TrendingUp className="w-8 h-8" /> Bilans & Rapports</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to={`/projects/${projectId}/finances`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm flex items-center gap-2">
            <BarChart2 className="w-4 h-4" /> Dashboard
          </Link>
          <Link to={`/projects/${projectId}/finances/collaborators`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm flex items-center gap-2">
            <Users className="w-4 h-4" /> Collaborateurs
          </Link>
          <Link to={`/projects/${projectId}/finances/payments`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Paiements
          </Link>
          <Link to={`/projects/${projectId}/finances/reports`} className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Bilans
          </Link>
        </div>

        {/* Add Button */}
        {canManage && (
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
            >
              + Créer un bilan
            </button>
          </div>
        )}

        {/* Reports List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : reports.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((report) => (
              <div 
                key={report.id} 
                className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => fetchReportDetail(report.id)}
              >
                <div className={`h-2 ${report.benefice_net >= 0 ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gradient-to-r from-red-400 to-red-500'}`}></div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${periodLabels[report.type_periode]?.color}`}>
                        {periodLabels[report.type_periode]?.icon} {periodLabels[report.type_periode]?.label}
                      </span>
                      <p className="text-gray-500 text-sm mt-2">
                        {formatDate(report.date_debut)} → {formatDate(report.date_fin)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${statusLabels[report.statut]?.color}`}>
                      {statusLabels[report.statut]?.icon} {statusLabels[report.statut]?.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Revenus</p>
                      <p className="text-lg font-bold text-green-600">{formatMoney(report.total_revenus)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Dépenses</p>
                      <p className="text-lg font-bold text-red-600">{formatMoney(report.total_depenses)}</p>
                    </div>
                  </div>

                  <div className={`p-3 rounded-xl ${report.benefice_net >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-xs text-gray-500">Bénéfice Net</p>
                    <p className={`text-2xl font-bold ${report.benefice_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMoney(report.benefice_net)}
                    </p>
                  </div>

                  {report.statut === 'valide' && (
                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <p className="text-xs text-gray-500">Distribué</p>
                        <p className="font-semibold text-purple-600">{formatMoney(report.benefice_distribue)}</p>
                      </div>
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <p className="text-xs text-gray-500">Réinvesti</p>
                        <p className="font-semibold text-blue-600">{formatMoney(report.benefice_reinvesti)}</p>
                      </div>
                    </div>
                  )}

                  {canManage && report.statut === 'brouillon' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleValidate(report.id); }}
                      className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Valider et calculer les distributions
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <TrendingUp className="w-16 h-16 mx-auto text-gray-400" />
            <h3 className="text-xl font-bold text-gray-900 mt-4">Aucun bilan</h3>
            <p className="text-gray-500 mt-2">Créez des bilans périodiques pour suivre la performance du projet</p>
            {canManage && (
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
              >
                Créer un bilan
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Report Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Plus className="w-5 h-5" /> Nouveau bilan</h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Type période */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de période</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(periodLabels).map(([key, val]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handlePeriodTypeChange(key)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.type_periode === key 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xl">{val.icon}</span>
                      <p className="font-medium text-sm mt-1">{val.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début *</label>
                  <input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_debut: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin *</label>
                  <input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_fin: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={3}
                  placeholder="Notes sur cette période..."
                ></textarea>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Créer le bilan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Report Detail Modal */}
      {showDetailModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${periodLabels[selectedReport.type_periode]?.color}`}>
                    {periodLabels[selectedReport.type_periode]?.icon} Bilan {periodLabels[selectedReport.type_periode]?.label}
                  </span>
                  <p className="text-gray-500 mt-1">
                    {formatDate(selectedReport.date_debut)} → {formatDate(selectedReport.date_fin)}
                  </p>
                </div>
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedReport(null); }}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-xs text-gray-500">Revenus</p>
                  <p className="text-xl font-bold text-green-600">{formatMoney(selectedReport.total_revenus)}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-xl">
                  <p className="text-xs text-gray-500">Dépenses</p>
                  <p className="text-xl font-bold text-red-600">{formatMoney(selectedReport.total_depenses)}</p>
                </div>
                <div className={`p-4 rounded-xl ${selectedReport.benefice_net >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                  <p className="text-xs text-gray-500">Bénéfice Net</p>
                  <p className={`text-xl font-bold ${selectedReport.benefice_net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                    {formatMoney(selectedReport.benefice_net)}
                  </p>
                </div>
              </div>

              {/* Distributions */}
              {selectedReport.distributions && selectedReport.distributions.length > 0 && (
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-4">📊 Distribution des bénéfices</h4>
                  <div className="space-y-3">
                    {selectedReport.distributions.map((dist, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold">
                            {dist.nom?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{dist.prenom} {dist.nom}</p>
                            <p className="text-sm text-gray-500">{dist.pourcentage_applique}% des bénéfices</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">{formatMoney(dist.montant_net)}</p>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                            dist.statut === 'paye' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {dist.statut === 'paye' ? '✅ Payé' : dist.statut === 'calcule' ? '⏳ À payer' : dist.statut}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <p className="text-xs text-gray-500">Total distribué</p>
                      <p className="text-xl font-bold text-purple-600">{formatMoney(selectedReport.benefice_distribue)}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="text-xs text-gray-500">Bénéfice réinvesti</p>
                      <p className="text-xl font-bold text-blue-600">{formatMoney(selectedReport.benefice_reinvesti)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedReport.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                  <p className="text-gray-600 bg-gray-50 rounded-xl p-4">{selectedReport.notes}</p>
                </div>
              )}

              {/* Actions */}
              {canManage && selectedReport.statut === 'brouillon' && (
                <button
                  onClick={() => handleValidate(selectedReport.id)}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" /> Valider et calculer les distributions
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectReports;
